import type { NumberLocale } from "../core/parser/locale";
import { parseAmount, parseDate } from "../core/parser/locale";
import type { BalanceInfo, BankTemplate, ColumnLayout } from "../core/parser/template";
import { textInXRange } from "../core/pdf/lines";
import type { TextLine } from "../core/types";

// Raiffeisen banka a.d. Beograd — RSD current-account card statement
// ("Izvod po tekućem računu"). Reverse-engineered from a real 8-page statement.
//
// Layout notes (PDF points, origin bottom-left):
//   Datum transakcije | Datum izvršenja | Broj kartice | Opis promene |
//   Iznos u ref. valuti | Iznos u orig. valuti | Isplata | Uplata | Stanje
// Amounts are US-style: comma = thousands, dot = decimal ("28,499.30"). Money
// leaving the account is in the "Isplata" column, money in is "Uplata"; every
// row prints a running "Stanje" balance. Multi-line memos wrap onto following
// lines within the description column; a page footer repeats a legalese block
// that must not be glued to the last transaction.

export const RAIFFEISEN_RS_LOCALE: NumberLocale = {
  decimalSeparator: ".",
  thousandsSeparator: ",",
};

export const RAIFFEISEN_RS_COLUMNS: ColumnLayout = {
  date: { xMin: 20, xMax: 92 }, // Datum transakcije
  valueDate: { xMin: 92, xMax: 150 }, // Datum izvršenja
  description: { xMin: 200, xMax: 335 }, // Opis promene (before "Iznos u ref. valuti")
  debit: { xMin: 428, xMax: 489 }, // Isplata (money out)
  credit: { xMin: 489, xMax: 542 }, // Uplata (money in)
  balance: { xMin: 542, xMax: 592 }, // Stanje (running balance)
};

const DATE_CELL = /^\d{2}\.\d{2}\.\d{4}$/;

export const raiffeisenRsTemplate: BankTemplate = {
  id: "rs-raiffeisen",
  name: "Raiffeisen banka a.d. Beograd",
  country: "RS",
  currency: "RSD",
  numberLocale: RAIFFEISEN_RS_LOCALE,
  dateFormats: ["DD.MM.YYYY"],
  layout: RAIFFEISEN_RS_COLUMNS,
  dateCellPattern: DATE_CELL,
  // Repeated page headers, the closing "STANJE" line and the legalese footer:
  // none are transactions and none must be glued as a continuation memo.
  stopRowPattern:
    /^STANJE\b|Nerealizovani|Za devizne|Za dinarske|prodajni\/kupovni|Po[šs]tovani|Opis promene|Datum prijema|Datum transakcije|Broj kartice|Strana:|Prethodno stanje/i,

  detect(fullText: string): number {
    const t = fullText.toLowerCase();
    let score = 0;
    if (t.includes("raiffeisen")) score += 0.7;
    if (t.includes("izvod po teku")) score += 0.2; // "Izvod po tekućem računu"
    if (t.includes("prethodno stanje")) score += 0.1;
    return Math.min(score, 1);
  },

  extractBalances(lines: TextLine[], locale: NumberLocale): BalanceInfo {
    const full = lines.map((l) => l.text).join("\n");

    const opening = matchAmount(full, /prethodno stanje[:\s]*([\d.,]+)/i, locale);

    // Closing: the footer prints a standalone uppercase "STANJE <amount>".
    // Fall back to the last transaction row's running balance if absent.
    const closing =
      matchAmount(full, /(?:^|\n)\s*STANJE\b[^\d\n]*([\d.,]+)/, locale) ??
      lastRowBalance(lines, locale);

    const period =
      /\bOd\s+(\d{2}\.\d{2}\.\d{4})\s+do\s+(\d{2}\.\d{2}\.\d{4})/i.exec(full);
    const account = /valuta:\s*[a-z]{3}\s+(\d{16,20})/i.exec(full);

    return {
      opening,
      closing,
      periodStart: period ? parseDate(period[1]!, ["DD.MM.YYYY"]) ?? undefined : undefined,
      periodEnd: period ? parseDate(period[2]!, ["DD.MM.YYYY"]) ?? undefined : undefined,
      accountId: account ? account[1] : undefined,
    };
  },
};

/** Running balance of the last transaction row — the de-facto closing balance. */
function lastRowBalance(lines: TextLine[], locale: NumberLocale): number | null {
  const B = RAIFFEISEN_RS_COLUMNS.balance!;
  const D = RAIFFEISEN_RS_COLUMNS.date;
  let last: number | null = null;
  for (const line of lines) {
    const dateCell = textInXRange(line, D.xMin, D.xMax);
    if (!DATE_CELL.test(dateCell)) continue;
    const bal = parseAmount(textInXRange(line, B.xMin, B.xMax), locale);
    if (bal != null) last = bal;
  }
  return last;
}

function matchAmount(text: string, re: RegExp, locale: NumberLocale): number | null {
  const m = re.exec(text);
  if (!m || !m[1]) return null;
  return parseAmount(m[1], locale);
}
